import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { FileCard, categoryOf } from "@/components/FileCard";
import { FilesTable } from "@/components/FilesTable";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/Button";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { FilterChips } from "@/components/FilterChips";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { Modal } from "@/components/Modal";
import { AttachmentUpload } from "@/components/AttachmentUpload";
import { toast } from "@/components/Toast";
import type { FileCardFile } from "@/components/FileCard";

// api.files returns FileRow[]: attachment columns plus project_name,
// uploader_name, and the related task (direct or via comment) for the
// card's task link.
function normalise(raw: {
  id: string;
  filename: string;
  mime_type?: string;
  size_bytes: number;
  uploaded_by: string;
  uploaded_at: string;
  project_id?: string;
  project_name?: string;
  related_task_id?: string | null;
  task_title?: string | null;
  uploader_name?: string | null;
  uploader_image?: string | null;
}): FileCardFile {
  const name = raw.filename ?? "";
  const ext = (name.split(".").pop() ?? "").toLowerCase();
  return {
    id: raw.id,
    name,
    ext,
    sizeBytes: raw.size_bytes ?? 0,
    uploadedAt: raw.uploaded_at ?? 0,
    uploader: {
      id: raw.uploaded_by ?? "?",
      name: raw.uploader_name ?? raw.uploaded_by ?? "Unknown",
      image: raw.uploader_image ?? null,
    },
    task:
      raw.related_task_id && raw.task_title
        ? { id: raw.related_task_id, title: raw.task_title }
        : null,
  };
}

type FilterValue = "all" | "pdf" | "doc" | "xls" | "img" | "other";

/**
 * Files page — designs/Files.html
 * Cross-project file browser: grid/list toggle, category filter chips, search.
 */
function FilesPage() {
  const [view, setView] = useState<"grid" | "list">("grid");
  const [filter, setFilter] = useState<FilterValue>("all");
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  // Step 1: pick a task to attach the file to. Files in this app are
  // always task-scoped — there's no "unattached files" concept — so the
  // global Files page has to ask which task before launching the picker.
  const [pickedTaskId, setPickedTaskId] = useState<string | null>(null);
  const qc = useQueryClient();

  // Grid view filters client-side, so it needs a broad pull (matching the old
  // LIMIT 200). The list view uses its own server-paginated query inside
  // <FilesTable>, so we only fetch this when the grid is showing.
  const q = useQuery({
    queryKey: queryKeys.files({ pageSize: 200 }),
    queryFn: () => api.listFiles({ pageSize: 200 }),
    enabled: view === "grid",
  });
  // Tasks across all projects the user can see — same source as the
  // command palette's task list.
  const tasksAll = useQuery({
    queryKey: queryKeys.tasksAll(),
    queryFn: api.tasksAll,
    enabled: uploadOpen,
  });

  // Normalise raw API rows to FileCardFile shape.
  const all: FileCardFile[] = (q.data?.files ?? []).map((f) => normalise(f));

  const needle = search.trim().toLowerCase();
  const items = all.filter(
    (f) =>
      (filter === "all" || categoryOf(f.ext) === filter) &&
      (needle === "" || f.name.toLowerCase().includes(needle)),
  );

  return (
    <div className="pg-wrap">
      <div className="pg-head">
        <h1>Files</h1>
        {view === "grid" && <span className="count">{items.length}</span>}
        <div className="pg-actions">
          <SegmentedToggle
            options={[
              { value: "grid", label: "Grid", icon: "board" },
              { value: "list", label: "List", icon: "clip" },
            ]}
            value={view}
            onChange={setView}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setPickedTaskId(null);
              setUploadOpen(true);
            }}
          >
            <Icon name="plus" size={14} /> Upload
          </Button>
        </div>
      </div>

      <div className="toolbar">
        <FilterChips
          options={[
            { value: "all", label: "All" },
            { value: "pdf", label: "PDFs" },
            { value: "doc", label: "Docs" },
            { value: "xls", label: "Sheets" },
            { value: "img", label: "Images" },
            { value: "other", label: "Other" },
          ]}
          value={filter}
          onChange={setFilter}
        />
        <div className="spacer" />
        <input
          type="search"
          className="input"
          placeholder="Search files…"
          aria-label="Search files"
          style={{ width: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {view === "list" ? (
        // Server-driven: own query + search/sort/pagination inside FilesTable.
        <FilesTable search={search} filter={filter} />
      ) : q.isLoading ? (
        <Skeleton count={6} height={120} />
      ) : items.length === 0 ? (
        <EmptyState
          icon="folder"
          title="No files yet"
          caption="Drop a file on any task to get started."
        />
      ) : (
        <div className="files-grid">
          {items.map((f) => (
            <FileCard key={f.id} file={f} />
          ))}
        </div>
      )}

      <Modal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Upload file to a task"
      >
        <p
          className="t-sm"
          style={{ margin: "0 0 12px", color: "var(--muted)" }}
        >
          Files attach to a task. Pick the task the upload should belong to,
          then choose a file.
        </p>
        <label
          htmlFor="files-upload-task"
          className="t-xs"
          style={{
            display: "block",
            marginBottom: 6,
            color: "var(--muted)",
          }}
        >
          Task
        </label>
        <select
          id="files-upload-task"
          className="input"
          style={{ width: "100%", marginBottom: 16 }}
          value={pickedTaskId ?? ""}
          onChange={(e) => setPickedTaskId(e.target.value || null)}
        >
          <option value="">Select a task…</option>
          {(tasksAll.data?.items ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.title}
            </option>
          ))}
        </select>
        {pickedTaskId ? (
          <AttachmentUpload
            taskId={pickedTaskId}
            onUploaded={() => {
              toast.success("File uploaded.");
              // The /api/files list is the cross-project aggregate; bust it so
              // the new file shows up in the grid the user just opened the
              // upload from.
              qc.invalidateQueries({ queryKey: queryKeys.files() });
              setUploadOpen(false);
            }}
          />
        ) : (
          <p className="t-xs" style={{ color: "var(--muted)", margin: 0 }}>
            Pick a task above to continue.
          </p>
        )}
      </Modal>
    </div>
  );
}

export const Route = createFileRoute("/files")({ component: FilesPage });
